import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

// assets
import AppstoreOutlined from '@ant-design/icons/AppstoreOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';

// ==============================|| 应用级联选择（对齐旧项目 AppCascaderSelect） ||=============================//
// 两级：应用分组（父级）/ 应用（子级）。
// - multiple=true（默认）：父级可整体勾选/取消（级联到子级），子级可单独勾选，多选。
// - multiple=false：单选，点击子级直接选中并关闭；clearable=false 时不可清空（固定选中一个）。
// options 兼容 { label, value } 与应用接口返回的 { name, id/app_id }。

const getOptionValue = (option = {}) => option.value ?? option.id ?? option.app_id ?? '';
const getOptionLabel = (option = {}) => option.label ?? option.name ?? option.raw_name ?? String(getOptionValue(option));

export default function AppCascaderSelect({
  value = [],
  onChange,
  options = [],
  placeholder = '请选择应用',
  disabled,
  width = '100%',
  multiple = true,
  clearable = true
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [keyword, setKeyword] = useState('');

  // 归一化选中值集合（多选为数组；单选允许单值/数组）
  const selectedValues = useMemo(() => {
    if (multiple) {
      return (Array.isArray(value) ? value : value === '' || value === undefined || value === null ? [] : [value]).map(String);
    }
    const v = Array.isArray(value) ? value[0] : value;
    return v === '' || v === undefined || v === null ? [] : [String(v)];
  }, [multiple, value]);

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);
  const selectedLabels = useMemo(
    () => (options || []).filter((option) => selectedSet.has(String(getOptionValue(option)))).map(getOptionLabel),
    [options, selectedSet]
  );

  const groups = useMemo(() => {
    const map = new Map();
    (options || []).forEach((option) => {
      const groupName = option.app_group_name || '其他';
      if (!map.has(groupName)) map.set(groupName, []);
      map.get(groupName).push(option);
    });
    return [...map.entries()];
  }, [options]);

  // 搜索过滤：组名命中显示整组；否则只显示名称命中的应用
  const filteredGroups = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return groups;
    return groups
      .map(([groupName, children]) => {
        if (groupName.toLowerCase().includes(kw)) return [groupName, children];
        const matched = children.filter((option) => String(getOptionLabel(option)).toLowerCase().includes(kw));
        return matched.length ? [groupName, matched] : null;
      })
      .filter(Boolean);
  }, [groups, keyword]);

  const closePopover = () => {
    setAnchorEl(null);
    setKeyword('');
  };

  const groupState = (children) => {
    const total = children.length;
    const checked = children.filter((option) => selectedSet.has(String(getOptionValue(option)))).length;
    return { checked: total > 0 && checked === total, indeterminate: checked > 0 && checked < total };
  };

  const toggleGroup = (children) => {
    if (!multiple) return;
    const allSelected = children.every((option) => selectedSet.has(String(getOptionValue(option))));
    const next = new Set(selectedSet);
    children.forEach((option) => {
      const key = String(getOptionValue(option));
      if (allSelected) next.delete(key);
      else next.add(key);
    });
    onChange([...next]);
  };

  const toggleApp = (option) => {
    if (multiple) {
      const next = new Set(selectedSet);
      const key = String(getOptionValue(option));
      if (next.has(key)) next.delete(key);
      else next.add(key);
      onChange([...next]);
      return;
    }

    // 单选：点击即选中并关闭；可清除时再次点击取消，不可清除时保持选中
    if (clearable && selectedSet.has(String(getOptionValue(option)))) {
      onChange('');
    } else {
      onChange(getOptionValue(option));
    }
    closePopover();
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onChange(multiple ? [] : '');
  };

  return (
    <>
      <Button
        component="div"
        variant="outlined"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchorEl)}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          // 固定宽度，不随选中内容撑开；内容过多时省略；传 '100%' 时撑满所在网格单元
          width: width === '100%' ? '100%' : typeof width === 'number' ? width : 220,
          maxWidth: '100%',
          justifyContent: 'space-between',
          height: 40,
          minWidth: 0,
          px: 1.5,
          textTransform: 'none',
          fontWeight: 400,
          color: 'text.primary',
          whiteSpace: 'nowrap',
          // 边框与其他筛选输入框一致（灰），不使用蓝色 primary
          borderColor: 'rgba(0, 0, 0, 0.23)',
          '&:hover': { borderColor: 'rgba(0, 0, 0, 0.23)' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1, overflow: 'hidden' }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', mr: 0.75, flexShrink: 0 }}>
            <AppstoreOutlined style={{ fontSize: '0.95rem', color: '#8c8c8c' }} />
          </Box>
          <Typography
            variant="body2"
            noWrap
            sx={{
              lineHeight: 1.4,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: selectedLabels.length ? 'text.primary' : 'text.secondary'
            }}
          >
            {selectedLabels.length ? selectedLabels.join('、') : placeholder}
          </Typography>
        </Box>
        {!multiple && clearable && selectedSet.size > 0 && (
          <IconButton size="small" onClick={handleClear} sx={{ ml: 0.5, p: 0.25, flexShrink: 0, color: 'text.secondary' }}>
            <CloseOutlined style={{ fontSize: '0.8rem' }} />
          </IconButton>
        )}
        {multiple && <Chip size="small" label={selectedLabels.length} sx={{ ml: 1, flexShrink: 0 }} />}
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: width === '100%' ? 320 : width, maxWidth: 360 } } }}
      >
        <Box sx={{ p: 1, pb: 0 }}>
          <TextField size="small" fullWidth placeholder="搜索应用" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </Box>
        <Box sx={{ maxHeight: 320, overflow: 'auto', p: 1 }}>
          {filteredGroups.length === 0 && (
            <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: 'center' }}>
              {options.length ? '无匹配应用' : '暂无应用'}
            </Typography>
          )}
          {filteredGroups.map(([groupName, children]) => {
            const state = groupState(children);
            return (
              <Stack key={groupName} spacing={0}>
                <FormControlLabel
                  control={
                    multiple ? (
                      <Checkbox
                        size="small"
                        checked={state.checked}
                        indeterminate={state.indeterminate}
                        onChange={() => toggleGroup(children)}
                      />
                    ) : (
                      <Checkbox size="small" sx={{ visibility: 'hidden' }} />
                    )
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {groupName}
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
                <Box sx={{ pl: 3.5 }}>
                  {children.map((option) => {
                    const checked = selectedSet.has(String(getOptionValue(option)));
                    return (
                      <FormControlLabel
                        key={getOptionValue(option)}
                        control={
                          multiple ? (
                            <Checkbox size="small" checked={checked} onChange={() => toggleApp(option)} />
                          ) : (
                            <Radio size="small" checked={checked} onChange={() => toggleApp(option)} />
                          )
                        }
                        label={<Typography variant="body2">{getOptionLabel(option)}</Typography>}
                        sx={{ m: 0, width: '100%' }}
                      />
                    );
                  })}
                </Box>
              </Stack>
            );
          })}
        </Box>
      </Popover>
    </>
  );
}

AppCascaderSelect.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  multiple: PropTypes.bool,
  clearable: PropTypes.bool
};
